import { normalizeDigits, toPersianDigits } from './persian-validation.js'; // eslint-disable-line import/extensions

// ----------------------------------------------------------------------

const NON_LOCALIZED_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'date',
  'datetime-local',
  'email',
  'file',
  'hidden',
  'image',
  'month',
  'password',
  'radio',
  'range',
  'reset',
  'submit',
  'time',
  'url',
  'week',
]);

const TECHNICAL_INPUT_MODES = new Set(['email', 'url']);

/** فیلدهای انسانی را از رمز، ایمیل و ورودی‌های فنیِ بدون تبدیل جدا می‌کند. */
export function shouldLocalizeInputDigits({
  type = 'text',
  inputMode,
  name,
  autoComplete,
  select = false,
  localizeDigits = true,
} = {}) {
  if (!localizeDigits || select || NON_LOCALIZED_INPUT_TYPES.has(type)) return false;
  // نمایش آشکار رمز، نوع ورودی را به متن تبدیل می‌کند؛ اما نباید حروف یا ارقام رمز را تغییر دهد.
  // یکسان‌سازی حروف رمز در سرور و براساس نسخه هش انجام می‌شود.
  if (/password$/i.test(name ?? '') || /(?:current|new)-password/.test(autoComplete ?? ''))
    return false;

  return !TECHNICAL_INPUT_MODES.has(inputMode);
}

/** ارقام و جداکننده‌های عدد را به قالب قابل پردازش تبدیل می‌کند. */
export function normalizeNumberInput(value) {
  const normalized = normalizeDigits(value)
    .replace(/[٫]/g, '.')
    .replace(/[−–—]/g, '-')
    .replace(/[٬,\s]/g, '')
    .replace(/[^0-9.-]/g, '');

  const isNegative = normalized.startsWith('-');
  const unsigned = normalized.replace(/-/g, '');
  const decimalIndex = unsigned.indexOf('.');
  const compact =
    decimalIndex === -1
      ? unsigned.replace(/\./g, '')
      : `${unsigned.slice(0, decimalIndex + 1)}${unsigned
          .slice(decimalIndex + 1)
          .replace(/\./g, '')}`;

  return `${isNegative ? '-' : ''}${compact}`;
}

/**
 * ارقام فیلد را برای نگهداری در فرم آماده می‌کند؛ برای رمز فراخوانی نشود.
 * ورودی عمومی ممکن است ایمیل یا کلید فایل باشد، پس حروف آن را تغییر نمی‌دهیم.
 * یکسان‌سازی حروف متن انسانی هنگام ذخیره در دامنه و مقایسه جستجو انجام می‌شود.
 */
export function normalizeLocalizedInput(value, { type, inputMode } = {}) {
  if (type === 'number') return normalizeNumberInput(value);

  const normalized = normalizeDigits(value);
  return inputMode === 'decimal' ? normalized.replace(/[٫]/g, '.') : normalized;
}

/** نمایش فارسی ورودی را از مقدار قابل پردازش آن جدا می‌کند. */
export function formatLocalizedInput(value, { type, inputMode } = {}) {
  if (value == null || (typeof value === 'number' && Number.isNaN(value))) return '';

  const localized = toPersianDigits(value);
  return type === 'number' || inputMode === 'decimal' ? localized.replace(/\./g, '٫') : localized;
}

/** مقدار اصلاح‌شده را بدون تغییر رویداد اصلی در اختیار فرم قرار می‌دهد. */
export function createNormalizedInputEvent(event, value) {
  const normalizedTarget = new Proxy(event.target, {
    get(target, property) {
      if (property === 'value') return value;

      const result = Reflect.get(target, property, target);
      return typeof result === 'function' ? result.bind(target) : result;
    },
  });

  return new Proxy(event, {
    get(target, property) {
      if (property === 'target' || property === 'currentTarget') return normalizedTarget;

      const result = Reflect.get(target, property, target);
      return typeof result === 'function' ? result.bind(target) : result;
    },
  });
}
